<template>
    <v-card class="elevation-0">
        <div class="detail-output">
            <div class="centerY justify-end" style="margin-bottom: 12px; gap: 8px; flex-wrap: wrap">
                <!--Filter time range-->
                <TimeRangePicker ref="TimeRangePicker" v-model="timeRange"></TimeRangePicker>
                <!--Filter status-->
                <div style="width: 250px;">
                    <v-select
                        hide-details outlined
                        v-model="filter.status"
                        placeholder="Status"
                        :items="statusList"
                        @input="loadOutputData(1, false)"
                        class="input-custom"
                        clearable
                    ></v-select>
                </div>
                <!--Filter phone-->
                <div style="width: 250px;">
                    <v-text-field
                        hide-details outlined
                        v-model="filter.phone"
                        @blur="loadOutputData(1, false)"
                        @keypress.enter="loadOutputData(1, false)"
                        placeholder="Phone or Call ID or Action code"
                        class="input-custom"
                    ></v-text-field>
                </div>
                <div
                    :class="isLoadingRefresh ? 'btn-disable' : 'btn-custom'"
                    @click="loadOutputData(1, false)"
                >
                    <v-progress-circular
                        indeterminate
                        width="3"
                        color="grey"
                        class="mr-6"
                        style="width: 16px"
                        v-if="isLoadingRefresh"
                    ></v-progress-circular>
                    <v-icon v-else style="margin-right: 6px; color: #FFFFFF; width: 16px">mdi-filter-outline</v-icon>
                    {{ $f.tc('filter') }} / {{ $f.tc('refresh') }}
                </div>
                <div
                    v-if="isEditable"
                    class="btn-export btn-custom"
                    @click="openExportReportEditor">
                    {{ $f.tc('export') }}
                </div>
            </div>
            <div class="campaign-active-output">
                <div class="clearfix campaign-active-output-table-wrap" v-if="tableData.length">
                    <div v-for="(table, index) in tableData"
                         :key="index"
                         :class="'campaign-active-output-table campaign-active-table-' + (index + 1)">
                        <SimpleDataTable
                            v-if="index < 2"
                            :table-title="table.title"
                            :headers="table.headers"
                            :items="table.data"
                            :config-column="true"
                            :hide-pager="true"
                            :current-page="filter.currentPage"
                            :total-page="totalPage"
                            :items-per-page="filter.itemPerPage"
                            :total-items="totalItems"
                            :keep-desktop-layout="true"
                            :add-index="index === 0"
                            @sort-change="updateOrderOutputData"
                            :sort-direction="filter.direction"
                        >
                            <template v-slot:record_url="props">
                                <div class="row-actions centerXY">
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <v-icon
                                                v-on="on"
                                                small
                                                class="action-icon"
                                                @click.stop="playAudio(props.item)"
                                            >{{
                                                    isAudioPlaying(props.item.record_url, false) ? 'mdi-stop' : 'mdi-play'
                                                }}
                                            </v-icon>
                                        </template>
                                        <span>{{ isAudioPlaying(props.item.record_url, false) ? 'Stop' : 'Play' }} bot audio</span>
                                    </v-tooltip>
                                    <v-tooltip top v-if="props.item.bridge">
                                        <template v-slot:activator="{ on }">
                                            <v-icon
                                                v-on="on"
                                                size="18"
                                                class="action-icon"
                                                color="black"
                                                @click.stop="playAudio(props.item, true)"
                                            >{{
                                                    isAudioPlaying(props.item.record_url, true) ? 'mdi-stop' : 'mdi-play'
                                                }}
                                            </v-icon>
                                        </template>
                                        <span>{{ isAudioPlaying(props.item.record_url, true) ? 'Stop' : 'Play' }} human audio</span>
                                    </v-tooltip>
                                </div>
                            </template>
                            <template v-slot:history="props">
                                <div class="row-actions centerXY">
                                    <span class="row-actions">
                                        <v-tooltip top>
                                            <template v-slot:activator="{ on }">
                                                <v-icon
                                                    v-on="on"
                                                    size="18"
                                                    class="action-icon"
                                                    @click.stop="showHistory(props.item)"
                                                >mdi-history</v-icon>
                                            </template>
                                            <span>{{ $f.tc('showHistory') }}</span>
                                        </v-tooltip>
                                    </span>
                                </div>
                            </template>
                        </SimpleDataTable>
                        <data-table-with-tab-nav
                            v-if="index >= 2"
                            :tab-title="table.title"
                            :data-table="table.data"
                            :data-headers="table.headers"
                            :columns-per-tab="4"
                            :default-tab="1"
                            :hide-pager="true"
                            :keep-desktop-layout="true"
                        >
                        </data-table-with-tab-nav>
                    </div>
                </div>
            </div>
            <div v-if="!tableData.length" class="text-xs-center" style="font-size: 14px !important;">
                <br><br><br>{{ $f.tc('noDataAvailable') }}<br><br><br><br><br>
            </div>
            <div class="campaign-active-output-pager">
                <SimpleDataTable
                    :headers="[]"
                    :items="[]"
                    :current-page="filter.currentPage"
                    :total-page="totalPage"
                    :items-per-page="filter.itemPerPage"
                    :total-items="totalItems"
                    @page-change="loadOutputData($event, filter.itemPerPage)"
                    @item-per-page-change="loadOutputData(1, $event)"
                >
                </SimpleDataTable>
            </div>
        </div>
        <v-slide-y-reverse-transition>
            <div v-if="audio" class="CampaignOutputPlayer centerXY">
                <audio :src="audio" controls="controls" :title="audioName"
                       ref="audio"></audio>
                <div class="btn-icon ml-2"
                     style="background-color: #FFFFFF"
                     @click="clearAudioData">
                    <v-icon>mdi-close</v-icon>
                </div>
            </div>
        </v-slide-y-reverse-transition>
        <ExportReportEditor ref="ExportReportEditor"/>
    </v-card>
</template>

<script>
import SimpleDataTable from '@/components/SimpleDataTable'
import CampaignService from '@/services/campaign.service'
import * as mime from 'mime-types'
import DataTableWithTabNav from '@/views/Campaign/DataTableWithTabNav'
import ExportReportEditor from '@/views/Campaign/ExportReportEditor'
import RoleMixin from '@/modules/fptai/mixins/RoleMixin'
import TimeRangePicker from '@/modules/fptai/components/TimeRangePicker'
import lodash from 'lodash'
import $const from '@/utils/const'

const showTimeRangeTypes = $const.ShowTimeRangeTypes

export default {
    name: 'CampaignDetailsOutput',
    components: {
        TimeRangePicker,
        ExportReportEditor,
        SimpleDataTable,
        DataTableWithTabNav,
    },
    mixins: [RoleMixin],
    props: {
        campaign: Object
    },
    data() {
        let today = this.$moment().format('YYYY-MM-DD')
        return {
            showTimeRangeTypes,
            statusList: [
                {text: 'Connected', value: 'connected'},
                {text: 'Unconnected', value: 'unconnected'},
                {text: 'In Black List', value: 'in_black_list'}
            ],
            tableData: [],
            totalPage: 1,
            totalItems: 0,
            audioName: '',
            audioURL: false,
            audio: false,
            audioExt: false,
            isEditable: RoleMixin.methods.canExportReport(this.campaign.permissions),
            filter: {
                itemPerPage: 10,
                currentPage: 1,
                fromDate: today,
                fromTime: '00:00:00',
                toDate: today,
                toTime: '23:59:59',
                status: '',
                phone: '',
                ordering: '',
                direction: '',
            },
            timeRange: 'today',
            isLoadingRefresh: false,
        }
    },
    mounted() {
        this.updateFilter()
        this.loadOutputData(this.filter.currentPage, this.filter.itemPerPage)
    },
    methods: {
        openExportReportEditor() {
            this.$refs.ExportReportEditor.open(this.campaign, this.filter)
        },
        updateOrderOutputData(obj) {
            this.filter.ordering = obj?.value || ''
            this.filter.direction = obj?.direction || ''
            this.loadOutputData(this.filter.currentPage, this.filter.itemPerPage, true)
        },
        loadOutputData(page, itemsPerPage, sort) {
            if (sort) {
                this.filter.direction = this.filter.direction === 'asc' ? 'desc' : 'asc'
            }

            if (this.isLoadingRefresh) {
                return
            }

            this.filter.currentPage = page ? page : this.filter.currentPage
            this.filter.itemPerPage = itemsPerPage ? itemsPerPage : this.filter.itemPerPage

            this.$appService.showLoading()
            this.isLoadingRefresh = true

            return new Promise((resolve, reject) => {
                CampaignService.getCampaignsOutput(
                    this.campaign.id,
                    this.filter
                ).then(res => {
                    this.tableData = []
                    this.totalItems = res.data.count || res.data.total
                    this.totalPage = Math.ceil(this.totalItems / this.filter.itemPerPage)
                    res.data.headers.map(header => {
                        header.children = this.$utils.reFormatHeaderTable(header.children).map(header => {
                            header.class = 'nowrap'
                            return header
                        })
                        let cacheHeader = localStorage.getItem(this.campaign.id)
                        if (cacheHeader?.length > 0 && header.value === 'calling_info') {
                            let cacheHeaderList = JSON.parse(cacheHeader)
                            if (!this.campaign.show_history) {
                                cacheHeaderList = cacheHeaderList.filter(item => item['value'] !== 'history')
                                localStorage.setItem(this.campaign.id, JSON.stringify(cacheHeaderList))
                            } else {
                                let historyHeaderObj = header.children.find(item => item['value'] === 'history')
                                if (!cacheHeaderList.find(item => item['value'] === 'history')) {
                                    cacheHeaderList.push(historyHeaderObj)
                                    localStorage.setItem(this.campaign.id, JSON.stringify(cacheHeaderList))
                                }
                            }
                            header.children = cacheHeaderList
                        }
                        let dataArr = []
                        res.data.results.map(data => {
                            dataArr.push(data[header.value])
                        })
                        this.tableData.push({
                            title: header.text,
                            headers: header.children,
                            data: dataArr
                        })
                    })
                    resolve(res)
                }, error => {
                    reject(error)
                }).finally(() => {
                    this.$appService.hideLoading()
                    this.isLoadingRefresh = false
                })
            })
        },
        updateDatetimeFilter(range = null) {
            let datetime = this.$refs.TimeRangePicker.getTimeRange(range)
            this.filter.fromDate = datetime.fromDate
            this.filter.fromTime = datetime.fromTime
            this.filter.toDate = datetime.toDate
            this.filter.toTime = datetime.toTime
        },
        playAudio(item, isBridge = false) {
            let url = item.record_url
            if (isBridge) {
                if (url.endsWith('/')) {
                    url = url.substring(0, url.length - 1)
                }
                url += '_bridge/'
            }
            if (url === this.audioURL) {
                this.clearAudioData()
                return
            } else {
                this.clearAudioData()
            }
            this.$appService.showLoading()
            this.$http.get(url, {responseType: 'blob'}).then(res => {
                this.audioURL = url
                this.audioName = this.$utils.getLastUrlSegment(url)
                this.$utils.convertBlobToDataURL(res.data).then(
                    data => {
                        this.audio = data
                        this.audioExt = mime.extension(res.data.type)
                        this.$appService.hideLoading()
                        setTimeout(() => {
                            this.$refs.audio.play()
                        }, 0)
                    }
                )
            }, () => {
                this.clearAudioData()
                this.$appService.hideLoading()
            })
        },
        clearAudioData() {
            this.audioURL = false
            this.audioName = ''
            this.audio = false
            this.audioExt = false
        },
        isAudioPlaying(audioURL = '', isBridge = false) {
            if (isBridge) {
                audioURL += '_bridge'
            }
            return audioURL === this.audioURL
        },
        updateFilter() {
            let query = this.$utils.cloneObject(this.$route.query)
            if (query.from && query.to && query.to >= query.from) {
                this.$refs.TimeRangePicker.updateCustomTime({from: query.from, to: query.to})
                this.$refs.TimeRangePicker.applyCustom()
                this.updateDatetimeFilter({from: query.from, to: query.to})
            }

            this.filter.phone = query.call_id || ''
        },
        showHistory(item) {
            let query = this.$utils.cloneObject(this.$route.query)
            query.call_id = item?.history
            query.from = this.$utils.convertToTimestamp(this.filter.fromDate, this.filter.fromTime)
            query.to = this.$utils.convertToTimestamp(this.filter.toDate, this.filter.toTime)
            if (!lodash.isEqual(query, this.$route.query)) {
                this.$router.replace({
                    query: query
                })
            }
        }
    },
    watch: {
        timeRange: {
            immediate: false,
            handler: function () {
                this.updateDatetimeFilter()
                this.loadOutputData(1, false)
            }
        }
    }
}
</script>

<style scoped lang="scss">
.CampaignOutputPlayer {
    position: fixed;
    width: 100%;
    height: auto;
    z-index: 999;
    left: 0;
    bottom: 0;
    text-align: center;
    padding: 15px 0;
    background: #b3d4fc;
    color: #fff;

    audio {
        width: 50%;
    }
}

.campaign-active-output {
    width: 100%;
    overflow: hidden;
    overflow-x: auto;
    border: 1px solid #96add0;
    border-top: none;
}

.action-icon {
    margin-right: 0 !important;
}

.btn-disable {
    font-family: "open sans", serif !important;
}

.campaign-active-output-table-wrap {
    display: flex;
}

.detail-output {
    padding: 20px;
}

.campaign-active-output-table {
    float: left;
    border: 1px solid #96add0;

    &:not(:first-child) {
        border-left: none;
    }

    &:first-child {
        border-left: none;
    }

    &:last-child {
        border-right: none;
    }
}

.datetime-box {
    display: flex;
    align-items: center;
    margin: 6px 6px 6px 0;
    padding: 24px 12px 12px 12px;
    border-radius: 5px;
    border: 1px solid #D9D9D9;
    position: relative;

    .label {
        position: absolute;
        left: 10px;
        top: -12px;
        font-size: 13px;
        font-family: "open sans semi", serif;
        background: #FFFFFF;
        padding: 0 6px;
    }
}

.campaign-active-table-3 {
    flex: 1 1;
    min-width: 350px;
}

::v-deep {
    input::placeholder {
        color: #0000008a !important;
    }

    .campaign-active-table-2 .table-title {
        margin: 0 1px;
    }

    .refresh-btn {
        margin: 0 16px 0 0;
        min-width: 40px;
    }

    .v-tabs__div.v-tab-disabled {
        align-items: stretch;
    }

    .v-text-field {
        margin-top: 0 !important;
        padding-top: 0 !important;
    }

    .v-text-field__details {
        display: none;
    }

    .campaign-detail-tabs .v-tabs__container {
        width: 100%;
        transform: none !important;
    }

    .v-input__slot {
        margin-bottom: 0 !important;
    }
}

.input-custom {
    ::v-deep {
        .v-input__slot {
            height: 32px;
            background: #FFFFFF;
            border-radius: 5px;
            border: 1px solid #D9D9D9;
            outline: none;
            font-size: 14px;
            padding: 6px 12px;
            margin: 0;
            display: flex;
            align-items: center;

            &:before, &:after {
                border: none !important;
            }
        }

        .v-label {
            background: #FFFFFF !important;
            padding: 0 6px;

            &:not(.v-label--active) {
                font-size: 14px !important;
                padding-left: 0 !important;
            }
        }
    }
}

@media screen and (max-width: 767px) {
    ::v-deep .output-filter {
        width: 100%;
    }
    ::v-deep .output-filter .date-field {
        width: 50%;
        margin-bottom: 16px;
    }
    ::v-deep .output-filter .btn-field,
    ::v-deep .output-filter .export-field {
        width: 100%;
        margin: 10px 0 0 0;
    }
    ::v-deep .output-filter .btn-field .v-btn {
        width: 100%;
    }
}
</style>
